import { Group, IconButton, iconSizes, Paper, Select, Stack, Switch, Text, TextInput } from '@common/ui'
import { IconPlus, IconTrash } from '@common/ui/icons'
import { com, kotlin } from '@eidu/web'
import ItemList from './ItemList'
import { keyOf, withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'
import NextElementDisplay from '../data/NextElementDisplay'
import localNext, { LocalNext, nextFromLocalNext } from '../../domain/form/definition/LocalNext'
import NextElementInput from './NextElementInput'
import { useOtherPages } from './OtherPagesProvider'
import NextOption from './NextOption'
import SingleChoice = com.eidu.sharedlib.form.definition.SingleChoice
import SingleChoiceStyle = com.eidu.sharedlib.form.definition.SingleChoiceStyle
import Option = com.eidu.sharedlib.form.definition.Option
import Item = com.eidu.sharedlib.form.definition.Item
import KtList = kotlin.collections.KtList

type SingleChoiceItemProps = {
  item: SingleChoice
  itemsOnPage: readonly NextOption[]
  onChange?: (item: SingleChoice) => void
}

const SingleChoiceItem: React.FC<SingleChoiceItemProps> = ({ item, itemsOnPage, onChange }) => {
  const otherPages = useOtherPages()
  const handleOptionTextChange = (index: number, text: string) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    const option = newOptions[index]
    newOptions[index] = withKey(new Option(text, option.items, option.next, option.response), option)
    onChange?.(
      withKey(
        item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.style, item.default, item.required),
        item
      )
    )
  }

  const handleOptionNextChange = (index: number, localNext: LocalNext | undefined) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    const option = newOptions[index]
    newOptions[index] = withKey(
      new Option(option.text, option.items, localNext ? nextFromLocalNext(localNext) : undefined, option.response),
      option
    )
    onChange?.(
      withKey(
        item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.style, item.default, item.required),
        item
      )
    )
  }

  const handleOptionItemsChange = (index: number, newItems: KtList<Item>) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    const option = newOptions[index]
    newOptions[index] = withKey(new Option(option.text, newItems, option.next, option.response), option)
    onChange?.(
      withKey(
        item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.style, item.default, item.required),
        item
      )
    )
  }

  const addOption = () => {
    const newOptions = [...item.options.asJsReadonlyArrayView(), new Option('New option', KtList.fromJsArray([]))]
    onChange?.(
      withKey(
        item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.style, item.default, item.required),
        item
      )
    )
  }

  const removeOption = (index: number) => {
    const newOptions = item.options.asJsReadonlyArrayView().filter((_, i) => i !== index)
    const newDefault =
      item.default !== null && item.default !== undefined
        ? item.default >= index
          ? Math.max(0, item.default - 1)
          : item.default
        : null
    onChange?.(
      withKey(
        item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.style, newDefault, item.required),
        item
      )
    )
  }

  return (
    <Paper withBorder radius="sm" p="xs">
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <TextInput
            style={{ flex: 1 }}
            label="Prompt"
            value={item.prompt}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(e.target.value, item.options, item.key, item.style, item.default, item.required),
                  item
                )
              )
            }
            disabled={!onChange}
          />
          <FormKeyField
            value={item.key}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(item.prompt, item.options, e.target.value, item.style, item.default, item.required),
                  item
                )
              )
            }
            disabled={!onChange}
          />
        </Group>
        <Group gap="md" wrap="nowrap" align="center">
          <Select
            label="Style"
            style={{ minWidth: 120 }}
            value={item.style.name}
            onChange={(value) =>
              value &&
              onChange?.(
                withKey(
                  item.copy(
                    item.prompt,
                    item.options,
                    item.key,
                    SingleChoiceStyle.valueOf(value),
                    item.default,
                    item.required
                  ),
                  item
                )
              )
            }
            data={[
              { value: SingleChoiceStyle.Radio.name, label: 'Radio' },
              { value: SingleChoiceStyle.DropDown.name, label: 'Dropdown' },
            ]}
            disabled={!onChange}
          />
          <Select
            label="Default"
            style={{ minWidth: 120 }}
            value={item.default !== null && item.default !== undefined ? String(item.default) : 'None'}
            onChange={(value) =>
              value &&
              onChange?.(
                withKey(
                  item.copy(
                    item.prompt,
                    item.options,
                    item.key,
                    item.style,
                    value === 'None' ? null : Number(value),
                    item.required
                  ),
                  item
                )
              )
            }
            data={[
              { value: 'None', label: '(None)' },
              ...item.options
                .asJsReadonlyArrayView()
                .map((option, index) => ({ value: String(index), label: option.text })),
            ]}
            disabled={!onChange}
          />
          <Switch
            checked={item.required}
            onChange={(e) =>
              onChange?.(
                withKey(
                  item.copy(item.prompt, item.options, item.key, item.style, item.default, e.target.checked),
                  item
                )
              )
            }
            disabled={!onChange}
            label="Required"
          />
        </Group>
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Options
          </Text>
          {item.options.asJsReadonlyArrayView().map((option, index) => {
            const next = localNext(option)

            return (
              <Paper withBorder radius="sm" p="xs" key={keyOf(option)}>
                <Stack gap="xs">
                  <Group gap="md" wrap="nowrap">
                    <TextInput
                      label={`Option ${index + 1}`}
                      value={option.text}
                      onChange={(e) => handleOptionTextChange(index, e.target.value)}
                      disabled={!onChange}
                      style={{ flex: 1 }}
                    />
                    {(!onChange && (
                      <NextElementDisplay value={next} options={[...otherPages, ...itemsOnPage]} type={'item'} />
                    )) || (
                      <NextElementInput
                        value={next}
                        parentKey={item.key + option.text}
                        type={'item'}
                        onChange={(newValue: LocalNext | undefined) => handleOptionNextChange(index, newValue)}
                        options={[...otherPages, ...itemsOnPage]}
                      />
                    )}
                    {onChange && (
                      <IconButton size="sm" aria-label="Remove option" onClick={() => removeOption(index)}>
                        <IconTrash size={iconSizes.sm} stroke={1.5} />
                      </IconButton>
                    )}
                  </Group>
                  <Paper radius="sm" p="xs" bg="var(--trellis-color-surface)">
                    <ItemList
                      items={option.items}
                      onChange={onChange && ((newItems) => handleOptionItemsChange(index, newItems))}
                    />
                  </Paper>
                </Stack>
              </Paper>
            )
          })}
          {onChange && (
            <IconButton size="sm" aria-label="Add option" onClick={addOption} style={{ alignSelf: 'center' }}>
              <IconPlus size={iconSizes.sm} stroke={1.5} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default SingleChoiceItem
